const { POLICY_APPLICATIONS, FLOW_POLICY_APPLICATIONS } = require('@kentik/ui-shared/alerting/constants');

/**
 * Canonical Application Format
 *
 * Throughout alerting, application is used to identify and segment policies, rules, alerts, and notifications.
 * For a while these were just strings: "core", "ddos", or "nms".
 *
 * In order to get more granular segments, we introduced a dot notation namespace, like an internet domain,
 * where the namespace goes from most to least specific, right to left.
 *
 * Subpolicies use a format like <tenantID>.mkp.{ddos|core}.
 * Ex: "1234.mkp.ddos" returns all ddos results for tenant 1234.
 * Ex: "*.mkp.ddos" returns all ddos results for all tenants.
 * Ex: "*.ddos" returns all results, tenant-agnostic.
 * Ex: "ddos" returns only results with an application exactly matching "ddos".
 *
 */

const TENANT_NAMESPACE = 'mkp';

/**
 * Application Umbrellas
 *
 * Several applications are variations of the same product and are presented as one group:
 * `core` and `cloud` are Traffic, and `nms`, `kmetrics` and `kevent` are NMS. Anywhere alerting
 * segments or labels by product rather than by exact application, it collapses through here.
 *
 * This is the inverse of getExpandedApplications, which takes an umbrella back out to its members.
 *
 * Umbrella values are themselves canonical applications, so they need no translation on the wire
 * and resolve directly against POLICY_APPLICATION_LABELS. Call sites that must emit a different
 * representative token for a legacy payload should map the umbrella explicitly at the boundary.
 *
 * @typedef {'core' | 'ddos' | 'nms'} ApplicationUmbrella
 */

/** @type {Readonly<{ CORE: 'core', DDOS: 'ddos', NMS: 'nms' }>} */
const APPLICATION_UMBRELLAS = Object.freeze({
  CORE: POLICY_APPLICATIONS.CORE,
  DDOS: POLICY_APPLICATIONS.DDOS,
  NMS: POLICY_APPLICATIONS.NMS
});

/** @type {Readonly<Record<string, ApplicationUmbrella>>} */
const UMBRELLA_BY_APPLICATION = Object.freeze({
  [POLICY_APPLICATIONS.CORE]: APPLICATION_UMBRELLAS.CORE,
  [POLICY_APPLICATIONS.CLOUD]: APPLICATION_UMBRELLAS.CORE,
  [POLICY_APPLICATIONS.DDOS]: APPLICATION_UMBRELLAS.DDOS,
  [POLICY_APPLICATIONS.NMS]: APPLICATION_UMBRELLAS.NMS,
  [POLICY_APPLICATIONS.METRIC]: APPLICATION_UMBRELLAS.NMS,
  [POLICY_APPLICATIONS.KEVENT]: APPLICATION_UMBRELLAS.NMS
});

/**
 * @param {string} [app]
 * @returns {string}
 */
function getTopLevelApplication(app) {
  return app?.split('.').pop() || POLICY_APPLICATIONS.CORE;
}

/**
 * The umbrella an application rolls up to, or undefined when it does not belong to one
 * (for example `synthetics`). Namespaced applications resolve through their top-level token,
 * so `1234.mkp.cloud` is Traffic.
 *
 * @param {string} [app]
 * @returns {ApplicationUmbrella | undefined}
 */
function getApplicationUmbrella(app) {
  return app === undefined || app === null ? undefined : UMBRELLA_BY_APPLICATION[getTopLevelApplication(app)];
}

/**
 * The distinct umbrellas a set of applications rolls up to, ignoring those without one.
 *
 * @param {Array<string | undefined>} [apps]
 * @returns {ApplicationUmbrella[]}
 */
function getApplicationUmbrellas(apps = []) {
  return Array.from(new Set(apps.map(getApplicationUmbrella).filter(Boolean)));
}

/**
 * True when an application carries more scope than its top-level token, e.g. `1234.mkp.cloud`.
 *
 * @param {string} [app]
 * @returns {boolean}
 */
function isNamespacedApplication(app) {
  return Boolean(app) && getTopLevelApplication(app) !== app;
}

function extractTenantId(app) {
  const parts = app?.split('.') || [];
  return parts[1] === TENANT_NAMESPACE ? parts[0] : null;
}

// Inputs often come from URL query strings or hashed filters, which may be arrays or other non-strings
const appEndsWith = (app, suffix) => typeof app === 'string' && app.endsWith(suffix);

// For strings
const isApplication = {
  isCore: (app) => appEndsWith(app, POLICY_APPLICATIONS.CORE) || app === '',
  isCloud: (app) => appEndsWith(app, POLICY_APPLICATIONS.CLOUD),
  isDdos: (app) => appEndsWith(app, POLICY_APPLICATIONS.DDOS),
  isNms: (app) => appEndsWith(app, POLICY_APPLICATIONS.NMS),
  isEvent: (app) => appEndsWith(app, POLICY_APPLICATIONS.KEVENT),
  isMetric: (app) => appEndsWith(app, POLICY_APPLICATIONS.METRIC),
  isSynth: (app) => appEndsWith(app, POLICY_APPLICATIONS.SYNTHETICS)
};

// For arrays
const checkApplication = Object.assign({}, isApplication, {
  hasCore: (apps = []) => apps?.some(isApplication.isCore),
  hasCloud: (apps = []) => apps?.some(isApplication.isCloud),
  hasDdos: (apps = []) => apps?.some(isApplication.isDdos),
  hasNms: (apps = []) => apps?.some(isApplication.isNms),
  hasEvent: (apps = []) => apps?.some(isApplication.isEvent),
  hasMetric: (apps = []) => apps?.some(isApplication.isMetric),
  hasSynth: (apps = []) => apps?.some(isApplication.isSynth)
});

function formatSubpolicyApplication(applicationString = '', tenantId) {
  if (!tenantId || tenantId === '0') {
    throw new Error('Cannot process subpolicy without a valid tenant ID');
  }

  const topLevelApp = getTopLevelApplication(applicationString);

  return `${tenantId}.${TENANT_NAMESPACE}.${topLevelApp}`;
}

function scopeApplicationsToTenant(applications, tenantId) {
  return applications?.length
    ? applications.map((app) => formatSubpolicyApplication(app, tenantId))
    : [formatSubpolicyApplication('', tenantId)];
}

function buildApplicationFilter(applications = [], options) {
  const { userGroup, includeSubpolicies, tenants = [] } = options || {};

  // CRITICAL: Prefix applications with tenant namespace to prevent cross-tenant data leakage
  if (userGroup) {
    return scopeApplicationsToTenant(applications, userGroup.id);
  }

  // Filtering by specific tenants (excludes non-tenant results)
  if (tenants?.length > 0) {
    return tenants.flatMap((tenantId) => applications.map((app) => formatSubpolicyApplication(app, tenantId)));
  }

  // Include any tenant results (*) in addition to non-tenant results
  if (includeSubpolicies) {
    return applications.concat(applications.map((app) => formatSubpolicyApplication(app, '*')));
  }

  return applications;
}

// This function takes in requested applications, and expands the list based on what's supported.
// For example, a request by the user for 'NMS' alerts implies `kevent`, `nms` and `kmetrics` applications.

function getExpandedApplications(requestedApplications = [], supportedApplications = []) {
  const base = requestedApplications?.length ? requestedApplications : FLOW_POLICY_APPLICATIONS;
  const appSet = new Set(base);
  const hasNmsOrMetric = checkApplication.hasNms(base) || checkApplication.hasMetric(base);

  if (hasNmsOrMetric) {
    [POLICY_APPLICATIONS.NMS, POLICY_APPLICATIONS.METRIC, POLICY_APPLICATIONS.KEVENT].forEach((app) => {
      if (supportedApplications.includes(app)) {
        appSet.add(app);
      }
    });
  }

  if (checkApplication.hasCore(base)) {
    appSet.add(POLICY_APPLICATIONS.CLOUD);
  }

  return Array.from(appSet);
}

module.exports = {
  extractTenantId,
  formatSubpolicyApplication,
  scopeApplicationsToTenant,
  buildApplicationFilter,
  getTopLevelApplication,
  checkApplication,
  getExpandedApplications,
  APPLICATION_UMBRELLAS,
  UMBRELLA_BY_APPLICATION,
  getApplicationUmbrella,
  getApplicationUmbrellas,
  isNamespacedApplication
};
